import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Fingerprint, Pencil, Trash2, Check, X } from 'lucide-react';
import { invoke } from '@tauri-apps/api/core';
import { isTauri } from '../utils/tauri';
import './Vault.css';

/**
 * The passkeys stored on one `password` item — list, rename, delete
 * (plans/25_07_2026/passkeys, W2345-SPEC-ui §4).
 *
 * **`passkey_list_for_item` is the only read path.** No React component may
 * pull `passkeys[]` out of `vault_get_item`: Rust strips the key material there
 * (`strip_passkey_secrets`), but the allowlisted ten-field projection
 * (`vault.rs` `passkey_metadata`) is the sanctioned surface and the one a
 * future field addition is reviewed against. Nothing here ever sees a private
 * key, and there is nothing it could do with one.
 *
 * `passkey_list_for_item` takes no `AppHandle` and therefore cannot lazily load
 * a cold `VaultState` — a locked vault is a normal render state here, not an
 * error. In practice this only mounts from the item modal, which is only
 * reachable from an unlocked vault.
 */

/** Rust rejects anything outside this (`MAX_PASSKEY_NAME_CHARS`, vault.rs). */
const MAX_NAME_CHARS = 64;

function humanDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleDateString();
}

export default function VaultPasskeyList({ itemId }) {
  const { t } = useTranslation();
  const [rows, setRows] = useState([]);
  const [state, setState] = useState('loading'); // loading | ready | locked | error
  const [error, setError] = useState(null);
  const [renaming, setRenaming] = useState(null); // credentialId
  const [draftName, setDraftName] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(null); // credentialId
  const [busy, setBusy] = useState(false);

  const load = useCallback(
    async (isCurrent = () => true) => {
      if (!itemId || !isTauri()) {
        if (!isCurrent()) return;
        setRows([]);
        setState('ready');
        return;
      }
      try {
        const list = await invoke('passkey_list_for_item', { itemId });
        if (!isCurrent()) return;
        setRows(Array.isArray(list) ? list : []);
        setState('ready');
        setError(null);
      } catch (e) {
        if (!isCurrent()) return;
        const message = String(e?.message || e || '');
        if (message.includes('vault_locked')) {
          setState('locked');
          return;
        }
        // "item not found" is the normal answer for an item that was deleted or
        // was never a password item — an empty list, not a failure banner.
        if (message.includes('item not found') || message.includes('not_found')) {
          setRows([]);
          setState('ready');
          return;
        }
        setError(message);
        setState('error');
      }
    },
    [itemId]
  );

  useEffect(() => {
    // A reply for the previous itemId must never land on the current one.
    let alive = true;
    setState('loading');
    setRenaming(null);
    setConfirmDelete(null);
    load(() => alive);
    return () => {
      alive = false;
    };
  }, [load]);

  const startRename = (row) => {
    setConfirmDelete(null);
    setRenaming(row.credentialId);
    setDraftName(row.name || '');
  };

  const commitRename = async (credentialId) => {
    const name = draftName.trim();
    if (busy) return;
    if (!name || name.length > MAX_NAME_CHARS) {
      setError(t('vault.passkeyNameLength', 'A passkey name must be 1-64 characters.'));
      return;
    }
    setBusy(true);
    try {
      await invoke('passkey_rename', { itemId, credentialId, name });
      setRenaming(null);
      setError(null);
      await load();
    } catch (e) {
      setError(String(e?.message || e));
    } finally {
      setBusy(false);
    }
  };

  const doDelete = async (credentialId) => {
    if (busy) return;
    setBusy(true);
    try {
      await invoke('passkey_delete', { itemId, credentialId });
      setConfirmDelete(null);
      setError(null);
      await load();
    } catch (e) {
      setError(String(e?.message || e));
    } finally {
      setBusy(false);
    }
  };

  if (state === 'loading') return null;

  if (state === 'locked') {
    return (
      <div className="vault-passkey-section">
        <span className="vault-passkey-section-title">{t('vault.passkeysHeading', 'Passkeys')}</span>
        <div className="vault-passkey-empty">
          {t('vault.passkeyListLocked', 'Unlock the vault to see this item’s passkeys.')}
        </div>
      </div>
    );
  }

  // Nothing to say about an item that holds none — the section stays out of the
  // form entirely rather than adding an empty block to every login item.
  if (state === 'ready' && rows.length === 0) return null;

  return (
    <div className="vault-passkey-section">
      <span className="vault-passkey-section-title">{t('vault.passkeysHeading', 'Passkeys')}</span>
      <div className="vault-passkey-rows">
        {rows.map((row) => {
          const isRenaming = renaming === row.credentialId;
          const isConfirming = confirmDelete === row.credentialId;
          return (
            <div key={row.credentialId} className="vault-passkey-row">
              <span className="vault-passkey-row-icon">
                <Fingerprint size={14} />
              </span>
              <div className="vault-passkey-row-texts">
                {isRenaming ? (
                  <input
                    type="text"
                    className="modal-input"
                    style={{ width: '100%' }}
                    value={draftName}
                    maxLength={MAX_NAME_CHARS}
                    autoFocus
                    onChange={(e) => setDraftName(e.target.value)}
                    onKeyDown={(e) => {
                      // This list renders inside VaultItemModal's <form>, so a
                      // bare Enter would submit and close the modal.
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        commitRename(row.credentialId);
                      } else if (e.key === 'Escape') {
                        e.preventDefault();
                        setRenaming(null);
                      }
                    }}
                  />
                ) : (
                  <>
                    <span className="vault-passkey-row-name">{row.name || row.rpId || ''}</span>
                    <span className="vault-passkey-row-sub">
                      {row.userName || t('vault.passkeyNoUsername', 'No username')}
                    </span>
                    <span className="vault-passkey-row-meta">
                      {t('vault.passkeyCreatedOn', 'Created {{date}}', {
                        date: humanDate(row.createdAt),
                      })}
                      {' · '}
                      {row.lastUsedAt
                        ? t('vault.passkeyLastUsed', 'Last used {{date}}', {
                            date: humanDate(row.lastUsedAt),
                          })
                        : t('vault.passkeyNeverUsed', 'Never used')}
                    </span>
                  </>
                )}
              </div>
              <div className="vault-passkey-row-actions">
                {isRenaming ? (
                  <>
                    <button
                      type="button"
                      className="vault-action-btn"
                      title={t('common.save')}
                      disabled={busy}
                      onClick={() => commitRename(row.credentialId)}
                    >
                      <Check size={13} />
                    </button>
                    <button
                      type="button"
                      className="vault-action-btn"
                      title={t('common.cancel')}
                      disabled={busy}
                      onClick={() => setRenaming(null)}
                    >
                      <X size={13} />
                    </button>
                  </>
                ) : isConfirming ? (
                  <>
                    <span className="vault-passkey-confirm">
                      {t(
                        'vault.passkeyDeleteConfirm',
                        'Delete? The private key is destroyed and cannot be recovered.'
                      )}
                    </span>
                    <button
                      type="button"
                      className="vault-action-btn vault-action-btn--danger"
                      disabled={busy}
                      onClick={() => doDelete(row.credentialId)}
                    >
                      {t('common.delete')}
                    </button>
                    <button
                      type="button"
                      className="vault-action-btn"
                      disabled={busy}
                      onClick={() => setConfirmDelete(null)}
                    >
                      {t('common.cancel')}
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      type="button"
                      className="vault-action-btn"
                      title={t('vault.passkeyRename', 'Rename passkey')}
                      onClick={() => startRename(row)}
                    >
                      <Pencil size={13} />
                    </button>
                    <button
                      type="button"
                      className="vault-action-btn vault-action-btn--danger"
                      title={t('vault.passkeyDelete', 'Delete passkey')}
                      onClick={() => {
                        setRenaming(null);
                        setConfirmDelete(row.credentialId);
                      }}
                    >
                      <Trash2 size={13} />
                    </button>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>
      {error && <div className="vault-modal-error">{error}</div>}
    </div>
  );
}
